import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Calendar, Clock, MapPin, Phone, Share2, Store, Users } from 'lucide-react'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import ReservationShareSheet from '../components/ReservationShareSheet'
import {
  buildReservationShareUrl,
  downloadReservationIcs,
  openGoogleCalendar,
} from '../utils/reservationShare'

type PublicReservation = Awaited<ReturnType<typeof api.getPublicReservation>>

/**
 * 公開予約共有ページ (#746) — 未ログインで閲覧可。
 *
 * `/r/{shortCode}` で来訪。grouプの幹事が LINE 等で共有したリンクの行き先。
 * バックエンド (`/api/v1/r/{shortCode}`) は PII (顧客名 / メール / phone /
 * paymentIntent / customerId 等) を除外した response を返すので、本ページが
 * 表示する内容も自動的に safe。
 *
 * 状態:
 *  - loading: spinner
 *  - 200: 予約 detail + .ics download / store map link
 *  - 404 (not_found): 「リンクが無効」
 *  - 410 (expired): 「リンクの有効期限切れ」
 */
export default function PublicReservation() {
  const { shortCode } = useParams<{ shortCode: string }>()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [data, setData] = useState<PublicReservation | null>(null)
  const [error, setError] = useState<'not_found' | 'expired' | 'unknown' | null>(null)
  const [loading, setLoading] = useState(true)
  const [shareOpen, setShareOpen] = useState(false)

  useEffect(() => {
    if (!shortCode) {
      setError('not_found')
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.getPublicReservation(shortCode)
        if (!cancelled) setData(res)
      } catch (e) {
        if (cancelled) return
        const code = (e as { message?: string })?.message
        if (code === 'not_found') setError('not_found')
        else if (code === 'expired') setError('expired')
        else setError('unknown')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [shortCode])

  const renderError = (key: 'not_found' | 'expired' | 'unknown') => (
    <div className="px-6 pt-16 text-center">
      <p className="font-display text-lg font-semibold text-text-primary mb-2">
        {t(`publicReservation.error.${key}.title`)}
      </p>
      <p className="font-body text-sm text-text-secondary mb-6">
        {t(`publicReservation.error.${key}.detail`)}
      </p>
      <button
        onClick={handleBack}
        className="rounded-xl bg-yokoso-red px-6 py-3 text-white font-display font-semibold tap-effect"
      >
        {t('publicReservation.error.cta')}
      </button>
    </div>
  )

  // back: 履歴があれば戻る (= my-reservations / 元の screen) / 無ければ home へ。
  // 共有 URL からの直接来訪 (window.history.length === 1) では home。
  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/home')
    }
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-20">
      <Header title={t('publicReservation.title')} onBack={handleBack} />

      {loading ? (
        <p className="text-center py-16 font-body text-sm text-text-tertiary">...</p>
      ) : error ? (
        renderError(error)
      ) : data ? (
        <div className="px-4 pt-4 space-y-4">
          {/* Status banner — cancelled / expired は赤、approved/pending は緑 */}
          {data.status === 'cancelled' && (
            <div className="rounded-xl bg-yokoso-red/10 px-4 py-3 text-yokoso-red font-body text-sm font-semibold text-center">
              {t('publicReservation.statusBanner.cancelled')}
            </div>
          )}
          {(data.status === 'rejected' || data.status === 'expired') && (
            <div className="rounded-xl bg-gray-200 px-4 py-3 text-text-secondary font-body text-sm font-semibold text-center">
              {t(`publicReservation.statusBanner.${data.status}`)}
            </div>
          )}

          {/* Store info */}
          <div className="bg-white rounded-2xl shadow-card p-4">
            <p className="font-display font-bold text-lg text-text-primary mb-1">
              {data.store.name || t('publicReservation.unknownStore')}
            </p>
            <div className="space-y-1 mb-3">
              {data.store.address && (
                <p className="font-body text-xs text-text-secondary">
                  <MapPin size={12} className="inline mr-1 -mt-0.5" />
                  {data.store.address}
                </p>
              )}
              {/* #1043: 店舗電話番号は公開情報 (顧客PIIとは別) なので常時表示 */}
              {data.store.phoneNumber && (
                <a
                  href={`tel:${data.store.phoneNumber}`}
                  className="font-body text-xs text-text-secondary flex items-center gap-1"
                >
                  <Phone size={12} className="shrink-0" />
                  {data.store.phoneNumber}
                </a>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {/* #1291: 共有先(未ログイン可)でも店舗詳細・コースを見られるように */}
              {data.store.id && (
                <button
                  onClick={() => navigate(`/stores/${data.store.id}`)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                >
                  <Store size={12} />
                  {t('publicReservation.viewStore')}
                </button>
              )}
              {data.store.googleMapsUrl && (
                <a
                  href={data.store.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                >
                  {t('publicReservation.openInMaps')}
                </a>
              )}
            </div>
          </div>

          {/* Reservation detail */}
          <div className="bg-white rounded-2xl shadow-card p-4 space-y-2.5">
            <div className="flex items-center gap-2 font-body text-sm text-text-primary">
              <Calendar size={16} className="text-text-secondary" />
              <span>{data.date}</span>
            </div>
            <div className="flex items-center gap-2 font-body text-sm text-text-primary">
              <Clock size={16} className="text-text-secondary" />
              <span>{data.time}</span>
            </div>
            <div className="flex items-center gap-2 font-body text-sm text-text-primary">
              <Users size={16} className="text-text-secondary" />
              <span>{t('publicReservation.partySize', { count: String(data.partySize) })}</span>
            </div>
            {data.courseName && (
              <div className="rounded-lg bg-gray-50 px-3 py-2 font-body text-sm text-text-primary">
                <p className="text-[10px] font-semibold text-text-tertiary mb-0.5">
                  {t('publicReservation.courseLabel')}
                </p>
                {data.courseName}
                {data.totalAmount !== null && data.totalAmount !== undefined && (
                  <span className="ml-1 text-text-secondary">
                    · {t('publicReservation.totalLabel', { amount: data.totalAmount.toLocaleString() })}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="space-y-2.5 pt-2">
            <button
              onClick={() => setShareOpen(true)}
              className="flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-yokoso-red text-white font-display font-semibold text-sm tap-effect"
            >
              <Share2 size={16} />
              {t('publicReservation.share')}
            </button>
            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={() =>
                  openGoogleCalendar({
                    storeName: data.store.name || '',
                    storeAddress: data.store.address || undefined,
                    date: data.date,
                    time: data.time,
                    partySize: data.partySize,
                    courseName: data.courseName || undefined,
                    paymentTiming: data.paymentTiming || undefined,
                    shortCode: data.shortCode,
                    shareUrl: buildReservationShareUrl(data.shortCode),
                  })
                }
                className="flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-text-primary font-display font-semibold text-sm tap-effect"
              >
                <Calendar size={16} className="text-text-secondary" />
                {t('publicReservation.openGoogleCalendar')}
              </button>
              <button
                onClick={() =>
                  downloadReservationIcs(
                    {
                      storeName: data.store.name || '',
                      storeAddress: data.store.address || undefined,
                      date: data.date,
                      time: data.time,
                      partySize: data.partySize,
                      courseName: data.courseName || undefined,
                      paymentTiming: data.paymentTiming || undefined,
                      shortCode: data.shortCode,
                      shareUrl: buildReservationShareUrl(data.shortCode),
                    },
                    `yokoso-${data.date}-${data.time.replace(':', '')}.ics`,
                  )
                }
                className="flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-text-primary font-display font-semibold text-sm tap-effect"
              >
                <Calendar size={16} className="text-text-secondary" />
                {t('publicReservation.downloadIcs')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {data && (
        <ReservationShareSheet
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          reservation={{
            storeName: data.store.name || '',
            storeAddress: data.store.address || undefined,
            date: data.date,
            time: data.time,
            partySize: data.partySize,
            courseName: data.courseName || undefined,
            paymentTiming: data.paymentTiming || undefined,
            shortCode: data.shortCode,
          }}
        />
      )}
    </div>
  )
}
